import { Link } from "react-router-dom";
import {
  Scale, Users, FileCheck2, BellRing, ArrowRight, CheckCircle2, Code2,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import WidgetPromoSection from "@/components/WidgetPromoSection";

const BENEFITS = [
  {
    icon: Users,
    title: "Your clients, one dashboard",
    body: "Clients invite you into their case with one click. Review every DS-260 answer, flag issues, and approve — across all your clients from a single Case Review dashboard.",
  },
  {
    icon: BellRing,
    title: "Cross-case red-flag inbox",
    body: "Every change a client makes after your review is flagged. See all red flags across every assigned case in one view — no more digging through emails.",
  },
  {
    icon: FileCheck2,
    title: "Client-ready case snapshots",
    body: "Download a printable case summary PDF for any client — readiness grade, document inventory, and consular timeline included.",
  },
  {
    icon: Scale,
    title: "You stay the attorney of record",
    body: "DS-260USA.com prepares and organizes. Legal strategy, review, and representation stay with you. We make your prep hours dramatically shorter.",
  },
];

export default function AttorneysPage() {
  return (
    <AppShell>
      <div data-testid="attorneys-page">
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-6 pt-14 pb-12">
          <div className="pill pill-ai mb-4">For immigration attorneys</div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            Consular cases, prepped before
            <br />
            <span style={{ color: "var(--action)" }}>they reach your desk.</span>
          </h1>
          <p className="text-base max-w-2xl mb-8" style={{ color: "var(--text-secondary)" }}>
            DS-260USA.com organizes your clients' civil documents, drafts their DS-260
            answers, and flags every post-review change — so your billable hours go to
            legal judgment, not data entry. Free for attorneys.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/register?attorney=1"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="attorneys-signup-cta"
            >
              Create your free attorney account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/widget"
              className="btn-ghost inline-flex items-center gap-2 text-sm"
              style={{ border: "1px solid var(--border-stone)" }}
              data-testid="attorneys-widget-link"
            >
              <Code2 className="w-4 h-4" /> Get the free Visa Bulletin widget
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-5 text-xs" style={{ color: "var(--text-secondary)" }}>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--success-text)" }} /> No cost to attorneys</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--success-text)" }} /> Clients invite you — no setup</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--success-text)" }} /> Bar number verified once</span>
          </div>
        </section>

        {/* Widget promo with live preview — the top-of-funnel hook */}
        <WidgetPromoSection slotId="attorneys-widget-preview-slot" />

        {/* Benefits */}
        <section className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="font-display text-lg font-bold mb-8" style={{ color: "var(--text-primary)" }}>
            What you get with an attorney account
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="card-surface p-6"
                data-testid={`attorneys-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center mb-3"
                  style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
                >
                  <b.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-bold mb-1.5" style={{ color: "var(--text-primary)" }}>
                  {b.title}
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-3xl px-6 pb-20 text-center">
          <h2 className="font-display text-lg font-bold mb-3" style={{ color: "var(--text-primary)" }}>
            Two minutes to sign up. Zero cost, ever.
          </h2>
          <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
            Register with your bar number, and your clients can start inviting you today.
          </p>
          <Link
            to="/register?attorney=1"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="attorneys-bottom-cta"
          >
            Create your attorney account <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </AppShell>
  );
}
